import useRestrictionRate from '../../../services/availability-and-rates/hooks/useRestrictionRate';
import useAvailability from '../../../services/availability-and-rates/hooks/useAvailability';


import { useMemo } from 'react';
import { RoomType } from '../../../services/room-type/response/GetAllRoomTypeResponse';
import { RatePlanResponse } from '../../../services/rate-plan/response/IndexRatePlanResponse';
import useIndexDynamicPricingGroups from '../../../services/dynamic-pricing-group/hooks/useIndexDynamicPricingGroups';
import { usePriceAdjustment } from '../../../services/dynamic-pricing-price-ajustment/hooks/usePriceAdjustment';
import { DynamicPricingGroupData } from '../../../services/dynamic-pricing-group/response/IndexDynamicPricingGroupResponse';

interface FormattedRate extends RatePlanResponse {
    restictions: [string, any][];  // Menggunakan array pasangan dari restrictions
}

export interface FormattedRoom extends RoomType {
    rates: FormattedRate[];               // Menggunakan array dari FormattedRate
    availability: [string, any][];     // Ketersediaan dalam format pasangan key-value
}

export interface FormattedMonthlySetup {
    data: FormattedRoom[];   // Array dari FormattedRoom
    isFetching: boolean;
    isError: boolean;
    isSuccess: boolean;
    isLoading: boolean;
    dynamicPricingGroups: DynamicPricingGroupData[];
    priceAdjustments: Record<string, any>;
}

export const useFormattedMonthlySetup = ({ startDate, endDate, property_id }: { startDate: string, endDate: string, property_id: string }, roomTypeQuery: any, ratePlanQuery: any): FormattedMonthlySetup => {
    // Fetch Dynamic Pricing Group dan Price Adjustment
    const { data: dynamicPricingGroupsData, isSuccess: isSuccessDynamicPricingGroups, isFetching: isFetchingDynamicPricingGroups } = useIndexDynamicPricingGroups({ property_id: property_id, is_active: true, include: 'items' });
    const { data: priceAdjustmentsData, isSuccess: isSuccessPriceAdjustments, isFetching: isFetchingPriceAdjustments } = usePriceAdjustment({ filter: { property_id: property_id, date: { gte: startDate, lte: endDate } } });

    const { data: restrictions, isFetching: isFetchingRestrictions, isError: isErrorRestrictions, isSuccess: isSuccessRestrictions, isLoading: isLoadingRestrictions } = useRestrictionRate({ gte: startDate, lte: endDate });
    const { data: availability, isFetching: isFetchingAvailability, isError: isErrorAvailability, isSuccess: isSuccessAvailability, isLoading: isLoadingAvailability } = useAvailability({ gte: startDate, lte: endDate });
    const { data: rates, isFetching: isFetchingRates, isError: isErrorRates, isSuccess: isSuccessRates, isLoading: isLoadingRates } = ratePlanQuery;
    const { data: rooms, isFetching: isFetchingRooms, isError: isErrorRooms, isSuccess: isSuccessRooms, isLoading: isLoadingRooms } = roomTypeQuery;

    const isFetching = isFetchingRestrictions || isFetchingAvailability || isFetchingRates || isFetchingRooms || isFetchingDynamicPricingGroups || isFetchingPriceAdjustments;
    const isError = isErrorRestrictions || isErrorAvailability || isErrorRates || isErrorRooms;
    const isSuccess = isSuccessRestrictions && isSuccessAvailability && isSuccessRates && isSuccessRooms && isSuccessDynamicPricingGroups && isSuccessPriceAdjustments;
    const isLoading = isLoadingRestrictions || isLoadingAvailability || isLoadingRates || isLoadingRooms || isFetchingDynamicPricingGroups || isFetchingPriceAdjustments;

    const formattedData = useMemo<FormattedRoom[]>(() => {
        if (!restrictions?.data || !availability?.data || !rates?.data || !rooms?.data) {
            return [];
        }

        return rooms.data.map((room: RoomType) => {
            const avlEntries = Object.entries(availability.data[room.id] ?? {});

            // const rateMap = rates.data.filter((rate) => rate.room_type_id === room.id).map((rate) => {
            //     const restictionsEntries = Object.entries(restrictions.data[rate.id] ?? {});

            //     return {
            //         ...rate,
            //         restictions: restictionsEntries
            //     } as FormattedRate;  // Menggunakan interface FormattedRate
            // });

            const rateMap = (() => {
                const filteredRates = rates.data.filter((rate: any) => rate.room_type_id === room.id);

                const rateMap: FormattedRate[] = [];
                const rateIndexMap = new Map<string, number>(); // Menyimpan index dari rate.id yang sudah dimasukkan
                const pendingChildren = new Map<string, FormattedRate[]>(); // Menyimpan child rates sementara jika parent belum ditemukan

                filteredRates.forEach((rate: any) => {
                    const restictionsEntries = Object.entries(restrictions.data[rate.id] ?? {});
                    const formattedRate: FormattedRate = { ...rate, restictions: restictionsEntries };

                    if (rate.parent_id && rateIndexMap.has(rate.parent_id)) {
                        // Jika ada parent_id dan parent sudah ada, letakkan setelahnya
                        const parentIndex = rateIndexMap.get(rate.parent_id)!;
                        rateMap.splice(parentIndex + 1, 0, formattedRate);

                        // Update indeks setelah penyisipan
                        rateIndexMap.set(rate.id, parentIndex + 1);
                    } else {
                        // Jika parent belum ada, simpan ke pendingChildren
                        if (rate.parent_id) {
                            if (!pendingChildren.has(rate.parent_id)) {
                                pendingChildren.set(rate.parent_id, []);
                            }
                            pendingChildren.get(rate.parent_id)!.push(formattedRate);
                        } else {
                            // Jika tidak ada parent_id, masukkan ke daftar utama
                            rateIndexMap.set(rate.id, rateMap.length);
                            rateMap.push(formattedRate);

                            // Jika ada child yang menunggu parent ini, tambahkan setelahnya
                            if (pendingChildren.has(rate.id)) {
                                pendingChildren.get(rate.id)!.forEach((child) => {
                                    rateMap.push(child);
                                    rateIndexMap.set(child.id, rateMap.length - 1);
                                });
                                pendingChildren.delete(rate.id);
                            }
                        }
                    }

                    // Perbarui indeks semua item setelah penyisipan
                    rateMap.forEach((item, index) => {
                        rateIndexMap.set(item.id, index);
                    });
                });

                return rateMap;
            })();

            console.log('rateMap', rateMap);

            return {
                ...room,
                rates: rateMap,
                availability: avlEntries
            } as FormattedRoom;  // Menggunakan interface FormattedRoom
        });
    }, [restrictions, availability, rates, rooms]);

    return {
        data: formattedData,
        isFetching: isFetching,
        isError: isError,
        isSuccess: isSuccess,
        isLoading: isLoading,
        dynamicPricingGroups: dynamicPricingGroupsData?.data || [],
        priceAdjustments: priceAdjustmentsData?.data || {}
    }
};